/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlinx.datetime.daysUntil
import models.*
import kotlin.math.*

/**
 * AI-powered portfolio optimizer that analyzes ALL savings goals together
 * and generates a unified allocation strategy across goals
 *
 * This replaces naive per-goal recommendations with intelligent portfolio-level optimization
 */
class GoalPortfolioOptimizer {

    /**
     * Generate a unified savings plan that allocates reductions across all goals
     * based on priority, deadline urgency, and amount
     *
     * @param actualMonthlySavings If provided, use this as the available amount instead of calculating from forecasts
     */
    fun generatePortfolioAllocationPlan(
        goals: List<SavingsGoal>,
        categorySpending: Map<String, Double>,
        monthlyIncome: Double? = null,
        expenseForecasts: Map<String, List<Double>>? = null,
        actualMonthlySavings: Double? = null
    ): PortfolioAllocationPlan {

        if (goals.isEmpty()) {
            return PortfolioAllocationPlan(
                totalMonthlySavingsNeeded = 0.0,
                totalAvailableForSavings = actualMonthlySavings ?: 0.0,
                spendingReductions = emptyList(),
                goalAllocations = emptyList(),
                isFeasible = true,
                optimizationScore = 0.0,
                forecastedMonthlyIncome = monthlyIncome ?: 0.0,
                forecastedMonthlyExpenses = 0.0,
                fundingGap = 0.0,
                message = "Add some savings goals to get started!",
                trendingCategories = emptyList()
            )
        }

        // Step 1: Calculate how much each goal needs per month
        val goalRequirements = calculateGoalRequirements(goals)
        val totalNeeded = goalRequirements.sumOf { it.monthlyRequired }

        // Step 2: Calculate total available from income - expenses OR use actual savings if provided
        val forecastedIncome = monthlyIncome ?: 0.0
        val forecastedExpenses = expenseForecasts?.values?.sumOf { it.average() }
            ?: categorySpending.values.sum()
        val calculatedAvailable = forecastedIncome - forecastedExpenses

        // Use actual monthly savings if provided, otherwise use calculated forecast
        val totalAvailable = actualMonthlySavings ?: calculatedAvailable

        println("💰 GoalPortfolioOptimizer: forecastedIncome = $forecastedIncome")
        println("💰 GoalPortfolioOptimizer: forecastedExpenses = $forecastedExpenses")
        println("💰 GoalPortfolioOptimizer: calculatedAvailable = $calculatedAvailable")
        println("💰 GoalPortfolioOptimizer: actualMonthlySavings = $actualMonthlySavings")
        println("💰 GoalPortfolioOptimizer: totalAvailable (used) = $totalAvailable")

        // Step 3: Calculate funding gap/surplus
        val fundingGap = totalNeeded - totalAvailable  // Positive = shortage, Negative = surplus

        // Step 4: Identify trending categories (top 3 by increase amount)
        val trendingCategories = identifyTrendingCategories(categorySpending, expenseForecasts)

        // Step 5: Generate friendly message
        val message = generateStatusMessage(fundingGap, totalAvailable, totalNeeded, trendingCategories)

        // Step 6: Optimize allocation based on priority, urgency, and feasibility
        val allocations = optimizeAllocation(
            goalRequirements = goalRequirements,
            totalAvailable = totalAvailable,
            priorityWeights = calculatePriorityWeights(goals)
        )

        // Step 7: Legacy reductions (keep for backward compatibility, but empty)
        val potentialReductions = emptyList<CategoryReduction>()
        val reductionsWithAllocations = emptyList<CategoryReductionWithAllocation>()

        return PortfolioAllocationPlan(
            totalMonthlySavingsNeeded = totalNeeded,
            totalAvailableForSavings = totalAvailable,
            spendingReductions = reductionsWithAllocations,
            goalAllocations = allocations,
            isFeasible = totalAvailable >= totalNeeded,
            optimizationScore = calculateOptimizationScore(allocations, goalRequirements),
            forecastedMonthlyIncome = forecastedIncome,
            forecastedMonthlyExpenses = forecastedExpenses,
            fundingGap = fundingGap,
            message = message,
            trendingCategories = trendingCategories
        )
    }

    /**
     * Calculate monthly savings requirement for each goal
     */
    private fun calculateGoalRequirements(goals: List<SavingsGoal>): List<GoalRequirement> {
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())

        return goals.map { goal ->
            val remaining = goal.targetAmount - goal.currentAmount
            val daysUntil = today.daysUntil(goal.deadline)
            val monthsUntil = maxOf(1, daysUntil / 30)

            val monthlyRequired = remaining / monthsUntil
            val urgencyScore = calculateUrgencyScore(monthsUntil, goal.priority)

            GoalRequirement(
                goal = goal,
                remainingAmount = remaining,
                monthsRemaining = monthsUntil,
                monthlyRequired = monthlyRequired,
                urgencyScore = urgencyScore,
                priorityWeight = getPriorityWeight(goal.priority)
            )
        }.sortedByDescending { it.urgencyScore * it.priorityWeight }
    }


    /**
     * Optimize allocation using AI-inspired weighted allocation algorithm
     * Considers priority, urgency, and feasibility
     */
    private fun optimizeAllocation(
        goalRequirements: List<GoalRequirement>,
        totalAvailable: Double,
        priorityWeights: Map<String, Double>
    ): List<GoalAllocation> {
        // If we have negative available funds (deficit), we can't allocate anything
        var remainingBudget = maxOf(0.0, totalAvailable)

        return goalRequirements.map { req ->
            // Calculate ideal allocation based on need and priority
            val idealAllocation = req.monthlyRequired

            // Calculate actual allocation based on available budget (never negative)
            val actualAllocation = if (remainingBudget <= 0.0) {
                0.0  // Can't allocate if no budget available
            } else {
                when {
                    // Must-have goals get priority - try to fully fund
                    req.goal.priority == GoalPriority.MUST_HAVE || req.goal.priority == GoalPriority.CRITICAL -> {
                        minOf(idealAllocation, remainingBudget)
                    }
                    // Nice-to-have gets proportional share
                    req.goal.priority == GoalPriority.NICE_TO_HAVE || req.goal.priority == GoalPriority.HIGH -> {
                        minOf(idealAllocation, remainingBudget * 0.7)
                    }
                    // Stretch goals get what's left
                    req.goal.priority == GoalPriority.STRETCH_GOAL || req.goal.priority == GoalPriority.MEDIUM -> {
                        minOf(idealAllocation, remainingBudget * 0.5)
                    }
                    // Maybe goals get minimal funding
                    else -> {
                        minOf(idealAllocation, remainingBudget * 0.3)
                    }
                }
            }

            remainingBudget = maxOf(0.0, remainingBudget - actualAllocation)

            GoalAllocation(
                goalId = req.goal.id,
                goalName = req.goal.name,
                monthlyAllocation = maxOf(0.0, actualAllocation),  // Ensure never negative
                percentageOfNeed = if (req.monthlyRequired > 0) {
                    maxOf(0.0, (actualAllocation / req.monthlyRequired) * 100)  // Clamp to 0% minimum
                } else 100.0,
                projectedCompletionMonths = if (actualAllocation > 0)
                    ceil(req.remainingAmount / actualAllocation).toInt()
                else Int.MAX_VALUE,
                priority = req.goal.priority,
                isFullyFunded = actualAllocation >= req.monthlyRequired
            )
        }
    }

    /**
     * Distribute a single category's savings across multiple goals
     */
    private fun distributeReductionAcrossGoals(
        reduction: CategoryReduction,
        allocations: List<GoalAllocation>
    ): List<GoalSplit> {
        val totalAllocation = allocations.sumOf { it.monthlyAllocation }
        if (totalAllocation == 0.0) return emptyList()

        var remainingSavings = reduction.suggestedReduction

        return allocations.map { allocation ->
            // Proportional split based on goal's share of total allocation
            val proportion = allocation.monthlyAllocation / totalAllocation
            val splitAmount = minOf(proportion * reduction.suggestedReduction, remainingSavings)
            remainingSavings -= splitAmount

            GoalSplit(
                goalId = allocation.goalId,
                goalName = allocation.goalName,
                amount = splitAmount,
                percentage = (splitAmount / reduction.suggestedReduction) * 100
            )
        }.filter { it.amount > 0.01 } // Filter out negligible splits
    }

    /**
     * Generate human-readable rationale for the recommendation
     */
    private fun generateRationale(
        reduction: CategoryReduction,
        splits: List<GoalSplit>
    ): String {
        val categoryName = reduction.category.replace("_", " ")
        val reductionPercent = (reduction.reductionPercentage * 100).roundToInt()

        return when (splits.size) {
            0 -> "Reducing $categoryName by $reductionPercent% could save $${reduction.suggestedReduction.roundToInt()}/month."
            1 -> {
                val goal = splits.first()
                "Reducing $categoryName by $reductionPercent% saves $${reduction.suggestedReduction.roundToInt()}/month → allocate to ${goal.goalName}."
            }
            else -> {
                val topGoals = splits.take(2).joinToString(" and ") { "${it.goalName} ($${it.amount.roundToInt()})" }
                "Reducing $categoryName by $reductionPercent% saves $${reduction.suggestedReduction.roundToInt()}/month → split between $topGoals."
            }
        }
    }

    // Helper functions for scoring and weights

    private fun calculateUrgencyScore(monthsRemaining: Int, priority: GoalPriority): Double {
        val timeUrgency = 1.0 / (monthsRemaining + 1.0) // More urgent as deadline approaches
        val priorityMultiplier = getPriorityWeight(priority)
        return timeUrgency * priorityMultiplier
    }

    private fun getPriorityWeight(priority: GoalPriority): Double = when (priority) {
        GoalPriority.MUST_HAVE, GoalPriority.CRITICAL -> 4.0
        GoalPriority.NICE_TO_HAVE, GoalPriority.HIGH -> 3.0
        GoalPriority.STRETCH_GOAL, GoalPriority.MEDIUM -> 2.0
        GoalPriority.MAYBE, GoalPriority.LOW -> 1.0
    }

    private fun calculatePriorityWeights(goals: List<SavingsGoal>): Map<String, Double> {
        return goals.associate { it.id to getPriorityWeight(it.priority) }
    }

    /**
     * Identify top 3 categories with increasing spending trends
     */
    private fun identifyTrendingCategories(
        categorySpending: Map<String, Double>,
        expenseForecasts: Map<String, List<Double>>?
    ): List<CategoryTrend> {
        if (expenseForecasts == null) return emptyList()

        val trends = categorySpending.mapNotNull { (category, current) ->
            val forecast = expenseForecasts[category] ?: return@mapNotNull null
            val forecastedAvg = forecast.average()

            CategoryTrend(
                category = category,
                currentAmount = current,
                projectedAmount = forecastedAvg
                // Other fields (increase, percent, isSignificant) are calculated automatically
            )
        }

        // Filter to only categories that are actually increasing, then sort by increase amount (descending), take top 3
        return trends
            .filter { it.increaseAmount > 0 }
            .sortedByDescending { it.increaseAmount }
            .take(3)
    }

    /**
     * Generate friendly status message based on funding gap and trends
     */
    private fun generateStatusMessage(
        fundingGap: Double,
        totalAvailable: Double,
        totalNeeded: Double,
        trendingCategories: List<CategoryTrend>
    ): String {
        // Round values consistently for display to avoid $1 discrepancies
        val roundedAvailable = totalAvailable.toInt()
        val roundedNeeded = totalNeeded.toInt()
        val roundedGap = roundedNeeded - roundedAvailable // Recalculate from rounded values
        val gapAbs = kotlin.math.abs(roundedGap)

        return when {
            roundedGap <= 0 -> {
                // Surplus case
                val surplus = gapAbs
                buildString {
                    append("🎉 Great news! You can fund all your goals with ")
                    append("$$$surplus extra per month available. ")
                    append("Consider adding new goals or boosting existing ones!")
                }
            }
            else -> {
                // Shortage case
                buildString {
                    append("💡 You're $$$gapAbs short per month to meet all goals. ")
                    append("Options: Adjust goal targets, look for expense reduction opportunities, or do a bit of both!")

                    if (trendingCategories.isNotEmpty()) {
                        append("\n\n📊 Heads up! These categories are trending up:")
                        trendingCategories.forEach { trend ->
                            val emoji = if (trend.isSignificant) " 📈" else ""
                            append("\n• ${trend.category}: +$${trend.increaseAmount.toInt()}/month (+${trend.increasePercent.toInt()}%)$emoji")
                        }
                        append("\n\nWorth reviewing for potential savings opportunities!")
                    }
                }
            }
        }
    }

    private fun getCategoryDifficulty(category: String): AdjustmentDifficulty = when (category.uppercase()) {
        "HOUSING", "UTILITIES", "INSURANCE" -> AdjustmentDifficulty.HARD
        "TRANSPORTATION", "HEALTHCARE", "GROCERIES" -> AdjustmentDifficulty.MODERATE
        "DINING_OUT", "ENTERTAINMENT", "SHOPPING", "SUBSCRIPTIONS" -> AdjustmentDifficulty.EASY
        else -> AdjustmentDifficulty.MODERATE
    }

    private fun getSafeReductionPercentage(category: String, difficulty: AdjustmentDifficulty): Double = when (difficulty) {
        AdjustmentDifficulty.EASY -> 0.40  // Can safely reduce by 40%
        AdjustmentDifficulty.MODERATE -> 0.25  // Moderate 25% reduction
        AdjustmentDifficulty.HARD -> 0.10  // Conservative 10% reduction
        AdjustmentDifficulty.EXTREME -> 0.05  // Very conservative 5% reduction
    }

    private fun calculateConfidenceScore(category: String, forecast: List<Double>?): Double {
        return if (forecast != null && forecast.size >= 3) {
            // Higher confidence with more historical data
            minOf(0.95, 0.6 + (forecast.size * 0.05))
        } else {
            0.60  // Lower confidence without forecast data
        }
    }

    private fun calculateOptimizationScore(
        allocations: List<GoalAllocation>,
        requirements: List<GoalRequirement>
    ): Double {
        // Score based on how well we're meeting high-priority goal needs
        val totalWeight = requirements.sumOf { it.priorityWeight }
        val achievedWeight = allocations.zip(requirements).sumOf { (alloc, req) ->
            // Clamp percentage to 0-100 range to prevent negative scores
            val normalizedPercentage = alloc.percentageOfNeed.coerceIn(0.0, 100.0)
            if (alloc.isFullyFunded) req.priorityWeight else req.priorityWeight * (normalizedPercentage / 100.0)
        }
        // Clamp final score to 0-100 range
        val score = if (totalWeight > 0) (achievedWeight / totalWeight) * 100 else 0.0
        return score.coerceIn(0.0, 100.0)
    }
}

/**
 * Data models for portfolio optimization
 */

data class GoalRequirement(
    val goal: SavingsGoal,
    val remainingAmount: Double,
    val monthsRemaining: Int,
    val monthlyRequired: Double,
    val urgencyScore: Double,
    val priorityWeight: Double
)

data class CategoryReduction(
    val category: String,
    val currentMonthlySpending: Double,
    val projectedMonthlySpending: Double,
    val recommendedMonthlySpending: Double,
    val suggestedReduction: Double,
    val reductionPercentage: Double,
    val difficulty: AdjustmentDifficulty,
    val confidenceScore: Double
)

data class GoalAllocation(
    val goalId: String,
    val goalName: String,
    val monthlyAllocation: Double,
    val percentageOfNeed: Double,
    val projectedCompletionMonths: Int,
    val priority: GoalPriority,
    val isFullyFunded: Boolean
)

data class GoalSplit(
    val goalId: String,
    val goalName: String,
    val amount: Double,
    val percentage: Double
)

data class CategoryReductionWithAllocation(
    val reduction: CategoryReduction,
    val goalSplits: List<GoalSplit>,
    val rationale: String
)

data class PortfolioAllocationPlan(
    val totalMonthlySavingsNeeded: Double,
    val totalAvailableForSavings: Double,
    val spendingReductions: List<CategoryReductionWithAllocation>,
    val goalAllocations: List<GoalAllocation>,
    val isFeasible: Boolean,
    val optimizationScore: Double,  // 0-100, higher is better

    // New fields for income-based budgeting
    val forecastedMonthlyIncome: Double = 0.0,
    val forecastedMonthlyExpenses: Double = 0.0,
    val fundingGap: Double = 0.0,  // Positive = shortage, Negative = surplus
    val message: String = "",  // Friendly status message
    val trendingCategories: List<CategoryTrend> = emptyList()  // Top 3 increasing categories
)
